import SEO from '../shared/SEO';
import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import BuyerForm from './BuyerForm';
import ShippingSelector from './ShippingSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import { showError } from '../../utils/sweetAlert';
import { clearCart, setWholesaleConfig, updateStockValidation } from '../../store/cartSlice';
import { getDisplayImageUrl } from '../../utils/imageUtils';
import { getStockIssueMessage, validateCartStock } from '../../utils/stockValidation';


const Checkout = () => {
  const cart = useSelector((state) => state.cart.items);
  const subtotal = useSelector((state) => state.cart.total);
  const wholesaleConfig = useSelector((state) => state.cart.wholesaleConfig);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [checkoutMode, setCheckoutMode] = useState('mercadopago');
  const totalQuantity = cart.reduce((totalItems, item) => totalItems + (Number(item.quantity) || 0), 0);
  const minimumPurchaseQuantity = wholesaleConfig?.minimumPurchaseQuantity || 4;
  const cartSignature = cart.map((item) => `${item._id}:${item.selectedVariant?.variantId || item.selectedVariant?.sku || ''}:${item.quantity}`).join('|');

  useEffect(() => {
    const loadConfigs = async () => {
      try {
        const [wholesaleRes, storeConfigRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/wholesale-config`),
          fetch(`${process.env.REACT_APP_API_URL}/store-config`)
        ]);
        const wholesaleData = await wholesaleRes.json();
        const storeConfigData = await storeConfigRes.json();

        if (wholesaleRes.ok && wholesaleData.data) {
          dispatch(setWholesaleConfig(wholesaleData.data));
        }
        if (storeConfigRes.ok && storeConfigData.data) {
          setCheckoutMode(storeConfigData.data.checkoutMode || 'mercadopago');
        }
      } catch (error) {
        console.error('Error cargando configuración:', error);
      }
    };

    loadConfigs();
  }, [dispatch]);

  useEffect(() => {
    if (cart.length && totalQuantity < minimumPurchaseQuantity) {
      showError(
        `La compra mínima es de ${minimumPurchaseQuantity} productos. Actualmente tenés ${totalQuantity}.`,
        'Cantidad mínima requerida'
      );
      navigate('/cart');
    }
  }, [cart.length, minimumPurchaseQuantity, navigate, totalQuantity]);

  useEffect(() => {
    if (!cart.length) return;
    let isCurrent = true;

    const validateCurrentStock = async () => {
      try {
        const result = await validateCartStock(cart);
        if (!isCurrent) return;
        dispatch(updateStockValidation(result));
        if (!result.valid) {
          showError(getStockIssueMessage(result.issues), 'Hay productos agotados en tu carrito');
          navigate('/cart');
        }
      } catch (error) {
        console.error('Error validando stock al iniciar checkout:', error);
      }
    };

    validateCurrentStock();
    return () => { isCurrent = false; };
  }, [cartSignature, dispatch, navigate]);

  // Estado para almacenar los datos del comprador desde BuyerForm
  const [buyerData, setBuyerData] = useState({
    nombre: '',
    mail: '',
    cedula: '',
    direccion: '',
    ciudad: '',
    departamento: '',
    codigoPostal: '',
    telefono: '',
    referencias: ''
  });

  // Estado para el tipo de envío
  const [shippingOption, setShippingOption] = useState({
    type: 'retiro_blandengues',
    label: 'Retiro en Local — Blandengues 1780',
    cost: 0,
    estimatedDays: ''
  });

  // Estado para el método de pago seleccionado
  const [paymentMethodInfo, setPaymentMethodInfo] = useState({ method: 'efectivo', label: 'Efectivo en local' });

  // Estado para notas adicionales
  const [notes, setNotes] = useState('');

  // Estado para errores de validación del formulario
  const [errors, setErrors] = useState({});

  // Calcular total con envío y recargo por método de pago
  const surchargeAmount = useMemo(() => {
    const rate = paymentMethodInfo?.surchargeRate || 0;
    return rate > 0 ? Math.round(subtotal * rate) : 0;
  }, [subtotal, paymentMethodInfo]);

  const total = useMemo(() => {
    return subtotal + (shippingOption?.cost || 0) + surchargeAmount;
  }, [subtotal, shippingOption, surchargeAmount]);

  const handleConfirmOrder = async () => {
    if (totalQuantity < minimumPurchaseQuantity) {
      showError(
        `La compra mínima es de ${minimumPurchaseQuantity} productos. Actualmente tenés ${totalQuantity}.`,
        'Cantidad mínima requerida'
      );
      navigate('/cart');
      return;
    }

    const { nombre, mail, cedula, direccion, ciudad, departamento, codigoPostal, telefono, referencias } = buyerData;
    
    const newErrors = {};
    let errorMessage = '';

    // Validar campos obligatorios
    if (!nombre) newErrors.nombre = true;
    if (!mail) newErrors.mail = true;
    if (!cedula) newErrors.cedula = true;

    // Validar correo con una expresión regular
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (mail && !emailRegex.test(mail)) {
      newErrors.mail = true;
      if (!errorMessage) errorMessage = 'Por favor, ingrese un correo electrónico válido.';
    }

    // Validar cédula (ejemplo: debe ser numérica y tener entre 7 y 10 dígitos)
    const cedulaRegex = /^\d{7,10}$/;
    if (cedula && !cedulaRegex.test(cedula)) {
      newErrors.cedula = true;
      if (!errorMessage) errorMessage = 'Por favor, ingrese una cédula válida (entre 7 y 10 dígitos).';
    }

    // Validar campos de dirección (excepto si es retiro en local)
    const isPickup = ['retiro_local', 'retiro_blandengues', 'retiro_ferrer_serra'].includes(shippingOption.type);
    if (!isPickup) {
      if (!direccion) newErrors.direccion = true;
      if (!ciudad) newErrors.ciudad = true;
      if (!telefono) newErrors.telefono = true;
    } else {
      // Para retiro en local solo necesitamos teléfono
      if (!telefono) newErrors.telefono = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      
      if (!errorMessage) {
        if (newErrors.nombre || (!mail && newErrors.mail) || (!cedula && newErrors.cedula)) {
          errorMessage = 'Por favor, complete todos los datos personales.';
        } else if (newErrors.direccion || newErrors.ciudad || newErrors.telefono) {
          if (!isPickup) {
            errorMessage = 'Por favor, complete todos los campos de dirección de envío.';
          } else {
            errorMessage = 'Por favor, ingrese un teléfono de contacto.';
          }
        }
      }
      
      showError(errorMessage);
      return;
    }
    
    setErrors({});

    try {
      const stockResult = await validateCartStock(cart);
      dispatch(updateStockValidation(stockResult));
      if (!stockResult.valid) {
        showError(getStockIssueMessage(stockResult.issues), 'Hay productos agotados en tu carrito');
        navigate('/cart');
        return;
      }
    } catch (error) {
      showError('No pudimos verificar el stock. Intentá nuevamente en unos segundos.', 'Stock no disponible');
      return;
    }

    try {
      // Preparar items del carrito para enviar
      const items = cart.map(item => ({
        _id: item._id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        selectedVariant: item.selectedVariant,
        variantInfo: item.variantInfo || null,
        selectedSizes: item.selectedSizes || [],
        selectedColors: item.selectedColors || [],
        isProductoAI: item.isProductoAI,
        isAIProduct: item.isAIProduct,
        images: item.images,
        image: item.image
      }));

      const isRetiro = ['retiro_local', 'retiro_blandengues', 'retiro_ferrer_serra'].includes(shippingOption.type);

      if (checkoutMode === 'reserva') {
        // Modo reserva: guardar pedido directamente sin pasar por MP
        const orderData = {
          items: items.map(item => ({
            _id: item._id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            variant: item.selectedVariant ? {
              variantId: item.selectedVariant.variantId,
              size: item.selectedVariant.size,
              color: item.selectedVariant.color,
              sku: item.selectedVariant.sku
            } : null,
            variantInfo: item.variantInfo || null,
            selectedSizes: item.selectedSizes || [],
            selectedColors: item.selectedColors || [],
            image: item.selectedVariant?.images?.[0] || item.images?.[0] || item.image
          })),
          subtotal,
          total,
          buyerInfo: { nombre, mail, cedula, telefono },
          shippingAddress: {
            direccion: isRetiro ? 'Retiro en local' : direccion,
            ciudad: isRetiro ? 'Montevideo' : ciudad,
            departamento,
            codigoPostal,
            telefono,
            referencias
          },
          shipping: {
            type: shippingOption.type,
            label: shippingOption.label,
            cost: shippingOption.cost,
            estimatedDays: shippingOption.estimatedDays
          },
          payment: { method: 'pending', status: 'pending' },
          paymentMethodInfo,
          notes
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/orders`,
          orderData
        );

        dispatch(clearCart());
        navigate('/orden-confirmada', {
          state: {
            orderNumber: response.data.orderNumber,
            items,
            subtotal,
            total,
            shippingOption,
            buyerInfo: { nombre, mail, cedula, telefono },
            paymentMethodInfo,
            notes
          }
        });
      } else {
        // Modo MercadoPago: flujo original
        const preferenceData = {
          items,
          subtotal,
          shippingType: shippingOption.type,
          shippingCost: shippingOption.cost,
          total,
          nombre,
          mail,
          cedula,
          direccion: isRetiro ? 'Retiro en local' : direccion,
          ciudad: isRetiro ? 'Montevideo' : ciudad,
          departamento,
          codigoPostal,
          telefono,
          referencias,
          notes
        };

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/mp/create_preference`,
          preferenceData
        );

        if (response.data.init_point) {
          window.location.href = response.data.init_point;
        } else {
          showError("Error al procesar el pago");
        }
      }
    } catch (error) {
      const responseData = error.response?.data;
      if (responseData?.code === 'MINIMUM_PURCHASE_NOT_MET' || responseData?.code === 'INVALID_QUANTITY') {
        showError(responseData.error, 'Cantidad mínima requerida');
        navigate('/cart');
        return;
      }
      const issues = responseData?.issues;
      if (issues?.length) {
        showError(getStockIssueMessage(issues), 'Hay productos agotados en tu carrito');
        navigate('/cart');
        return;
      }
      showError(checkoutMode === 'reserva' ? 'Ocurrió un error al registrar el pedido' : 'Ocurrió un error al procesar el pago');
    }
  };

  const handleBackToCart = () => {
    navigate('/cart');
  };

  return (
    <>
    <SEO title="Finalizar Compra" />
    <div className="container wrapper mt-5">
      {cart.length === 0 ? (
        <div className="checkout-empty">
          <div className="text-center py-5">
            <i className="fa-solid fa-cart-shopping" style={{ fontSize: '4rem', color: '#ccc', marginBottom: '20px' }}></i>
            <h2 className="mb-3">No hay productos en tu carrito</h2>
            <p className="text-muted mb-4">Agrega algunos productos antes de proceder al checkout</p>
            <button 
              className="add-to-cart-btn px-5 py-3 shadow-sm"
              style={{ fontSize: '1rem', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 'bold' }}
              onClick={() => navigate('/tienda')}
            >
              <i className="fa-solid fa-store me-2"></i>
              Ir a la tienda
            </button>
          </div>
        </div>
      ) : (
        <div className="checkout-container">
          {/* Header */}
          <div className="checkout-header">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h1 className="checkout-title">
                  <i className={`fa-solid ${checkoutMode === 'reserva' ? 'fa-clipboard-list' : 'fa-credit-card'} me-3`}></i>
                  {checkoutMode === 'reserva' ? 'Realizar Pedido' : 'Finalizar Compra'}
                </h1>
                <p className="checkout-subtitle">
                  {checkoutMode === 'reserva'
                    ? 'Completá tus datos y reservá tu pedido. Te contactaremos para coordinar el pago.'
                    : 'Revisá tu pedido y completá tus datos'}
                </p>
              </div>
              <div className="col-md-6 text-md-end">
                <div className="checkout-progress">
                  <span className="progress-step active">1. Revisión</span>
                  <span className="progress-arrow">→</span>
                  <span className="progress-step active">2. Datos</span>
                  <span className="progress-arrow">→</span>
                  <span className="progress-step">{checkoutMode === 'reserva' ? '3. Reserva' : '3. Pago'}</span>
                </div>
              </div>
            </div>

            {checkoutMode === 'reserva' && (
              <div
                style={{
                  background: '#fff8e1',
                  border: '1px solid #ffe082',
                  borderRadius: '4px',
                  padding: '12px 18px',
                  marginTop: '16px',
                  fontSize: '0.88rem',
                  color: '#5a4a00'
                }}
              >
                <i className="fa-solid fa-circle-info me-2"></i>
                <strong>Tienda en modo pedido:</strong> Tu pedido quedará registrado como reserva. El equipo te contactará para coordinar el pago y la entrega.
              </div>
            )}
          </div>

          <div className="row">
            {/* Order Summary */}
            <div className="col-lg-7">
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-list me-2"></i>
                    Resumen del Pedido
                  </h4>
                  <span className="item-count">
                    {cart.reduce((acc, item) => acc + item.quantity, 0)} productos
                  </span>
                </div>
                
                <div className="order-items">
                  {cart.map((item) => (
                    <div key={`${item._id}-${item.selectedVariant?.sku || 'no-variant'}`} className="checkout-item">
                      <div className="row align-items-center">
                        <div className="col-2">
                          <img 
                            src={getDisplayImageUrl(item.selectedVariant?.images?.[0] || item.images?.[0] || item.image)} 
                            alt={item.name}
                            className="checkout-item-image"
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/80?text=Sin+Imagen';
                            }}
                          />
                        </div>
                        <div className="col-6">
                          <h6 className="item-name">{item.name}</h6>
                          {item.selectedVariant && (
                            <div className="item-variants">
                              {item.selectedVariant.size && <span className="variant-tag size">{item.selectedVariant.size}</span>}
                              {item.selectedVariant.color && <span className="variant-tag color">{item.selectedVariant.color}</span>}
                              {item.selectedVariant.productType && <span className="variant-tag">{item.selectedVariant.productType}</span>}
                            </div>
                          )}
                          {!item.selectedVariant && item.variantInfo && (
                            <div className="item-variants">
                              <span className="variant-tag">{item.variantInfo}</span>
                            </div>
                          )}
                          {item.selectedSizes?.length > 0 && (
                            <div className="item-variants">
                              {item.selectedSizes.map((s, i) => <span key={i} className="variant-tag size">{s}</span>)}
                            </div>
                          )}
                          {item.selectedColors?.length > 0 && (
                            <div className="item-variants">
                              {item.selectedColors.map((c, i) => <span key={i} className="variant-tag color">{c}</span>)}
                            </div>
                          )}
                          {item.selectedVariant?.sku && (
                            <small className="item-sku">SKU: {item.selectedVariant.sku}</small>
                          )}
                        </div>
                        <div className="col-2 text-center">
                          <span className="item-quantity">×{item.quantity}</span>
                        </div>
                        <div className="col-2 text-end">
                          <span className="item-price">${item.price * item.quantity}</span>
                          <small className="item-unit-price">${item.price} c/u</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Shipping Selector */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-truck me-2"></i>
                    Método de Envío
                  </h4>
                </div>
                <ShippingSelector 
                  selectedShipping={shippingOption.type}
                  onShippingChange={setShippingOption}
                  ciudad={buyerData.ciudad}
                />
              </div>

              {/* Buyer Form */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-user me-2"></i>
                    Datos del Comprador
                  </h4>
                </div>
                <BuyerForm 
                  onChange={(data) => {
                    setBuyerData(data);
                    if (Object.keys(errors).length > 0) {
                      setErrors({});
                    }
                  }} 
                  shippingType={shippingOption.type}
                  errors={errors}
                />
              </div>

              {/* Método de Pago */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-wallet me-2"></i>
                    Método de Pago
                  </h4>
                </div>
                <PaymentMethodSelector
                  selectedMethod={paymentMethodInfo.method}
                  onMethodChange={setPaymentMethodInfo}
                  checkoutMode={checkoutMode}
                />
              </div>

              {/* Notas adicionales */}
              <div className="checkout-section">
                <div className="section-header">
                  <h4>
                    <i className="fa-solid fa-comment me-2"></i>
                    Notas del Pedido (opcional)
                  </h4>
                </div>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Instrucciones especiales, horarios de entrega preferidos, etc."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </div>

            {/* Payment Summary */}
            <div className="col-lg-5">
              <div className="checkout-sidebar">
                <div className="payment-summary">
                  <div className="summary-header">
                    <h4>
                      <i className="fa-solid fa-calculator me-2"></i>
                      Total del Pedido
                    </h4>
                  </div>
                  
                  <div className="summary-content">
                    <div className="summary-row">
                      <span>Subtotal ({cart.reduce((acc, item) => acc + item.quantity, 0)} productos)</span>
                      <span>${subtotal}</span>
                    </div>
                    <div className="summary-row">
                      <span>
                        <i className="fa-solid fa-truck me-1"></i>
                        {shippingOption.label}
                      </span>
                      {shippingOption.cost === 0 ? (
                        <span className="free-text">Gratis</span>
                      ) : (
                        <span>${shippingOption.cost}</span>
                      )}
                    </div>
                    <div className="summary-row shipping-time">
                      <small className="text-muted">
                        <i className="fa-regular fa-clock me-1"></i>
                        {shippingOption.estimatedDays}
                      </small>
                    </div>
                    {surchargeAmount > 0 && (
                      <div className="summary-row">
                        <span>
                          <i className="fa-solid fa-percent me-1"></i>
                          Recargo {paymentMethodInfo.label}
                        </span>
                        <span>+${surchargeAmount}</span>
                      </div>
                    )}
                    <div className="summary-row">
                      <span>Impuestos</span>
                      <span>Incluidos</span>
                    </div>
                    <div className="summary-divider"></div>
                    <div className="summary-row total">
                      <strong>Total a Pagar</strong>
                      <strong className="total-amount">${total}</strong>
                    </div>
                  </div>

                  <div className="payment-actions">
                    <button 
                      className="add-to-cart-btn w-100 py-3 mb-3"
                      onClick={handleConfirmOrder}
                    >
                      <i className={`fa-solid ${checkoutMode === 'reserva' ? 'fa-clipboard-check' : 'fa-credit-card'} me-2`}></i>
                      {checkoutMode === 'reserva' ? 'CONFIRMAR PEDIDO' : 'PAGAR AHORA'}
                    </button>
                    
                    <button 
                      className="btn btn-outline-secondary w-100 py-2"
                      onClick={handleBackToCart}
                    >
                      <i className="fa-solid fa-arrow-left me-2"></i>
                      Volver al Carrito
                    </button>
                  </div>

                  <div className="security-badges">
                    <div className="security-item">
                      <i className="fa-solid fa-shield-halved"></i>
                      <span>Pedido Seguro</span>
                    </div>
                    {checkoutMode === 'reserva' ? (
                      <>
                        <div className="security-item">
                          <i className="fa-solid fa-handshake"></i>
                          <span>Pago al confirmar</span>
                        </div>
                        <div className="security-item">
                          <i className="fa-solid fa-phone"></i>
                          <span>Te contactamos</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="security-item">
                          <i className="fa-solid fa-credit-card"></i>
                          <span>Mercado Pago</span>
                        </div>
                        <div className="security-item">
                          <i className="fa-solid fa-undo"></i>
                          <span>Garantía de devolución</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default Checkout;
